import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ListChecks, Loader2, TriangleAlert } from 'lucide-react';
import { useTasks } from '../../lib/tasks';
import { formatRelative } from '../../lib/format';
import { SHEET_PANEL } from './panel';

/**
 * The tasks tray: what this tab has running, and how the last few ended.
 *
 * Its own icon rather than a corner of the notifications bell, because the two
 * answer different questions. The bell is the PLATFORM's news — open findings,
 * a bill about to fall due — and it is the same for everyone who can see it.
 * This is what YOU just started, in this tab: a resync you pressed forty
 * seconds ago and walked away from.
 *
 * The badge counts what is RUNNING, and spins while anything is, so the answer
 * to "did that button do anything" is always in the header. Finished tasks
 * stay in the list after their bubble goes, because dismissing a toast means
 * "I saw it", not "that never happened".
 */
const STATE_ICON = {
  running: <Loader2 className="size-3.5 animate-spin text-muted" />,
  ok: <CheckCircle2 className="size-3.5 text-success" />,
  failed: <TriangleAlert className="size-3.5 text-danger" />,
} as const;

export function TasksMenu() {
  const { tasks, running, clearFinished } = useTasks();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [open]);

  /*
    Nothing started, nothing to say. An always-present icon with a permanent
    zero is a control that teaches people it never has anything in it.
  */
  if (tasks.length === 0) return null;

  const newestFirst = [...tasks].reverse();

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface-2 px-2.5 py-1.5 text-xs text-ink hover:bg-surface-3"
        title={running > 0 ? `${running} task${running === 1 ? '' : 's'} running` : 'Recent tasks'}
      >
        {running > 0 ? (
          <Loader2 className="size-3.5 animate-spin text-primary" />
        ) : (
          <ListChecks className="size-3.5 text-muted" />
        )}
        <span className="hidden sm:inline">Tasks</span>
        {running > 0 && (
          <span className="rounded-full bg-primary px-1.5 text-[10px] font-medium text-white">
            {running}
          </span>
        )}
      </button>

      {open && (
        <div
          className={`z-30 overflow-hidden rounded-xl border border-line bg-surface shadow-xl ${SHEET_PANEL} sm:top-auto sm:mt-2 sm:w-80`}
        >
          <div className="flex items-center justify-between border-b border-line px-4 py-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-faint">Tasks</span>
            {tasks.some((t) => t.state !== 'running') && (
              <button onClick={clearFinished} className="text-xs text-link hover:underline">
                Clear finished
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {newestFirst.map((t) => (
              <div key={t.id} className="flex items-start gap-2 px-4 py-2.5 hover:bg-surface-2">
                <span className="mt-0.5 shrink-0">{STATE_ICON[t.state]}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-ink">{t.label}</p>
                  <p className="text-xs text-muted">
                    {t.state === 'running' ? 'Running…' : t.message}
                  </p>
                  <p className="mt-0.5 text-[11px] text-faint">
                    {formatRelative(t.finishedAt ?? t.startedAt)}
                  </p>
                </div>
                {t.href && t.state !== 'running' && (
                  <Link
                    to={t.href}
                    onClick={() => setOpen(false)}
                    className="shrink-0 text-xs text-link hover:underline"
                  >
                    Open
                  </Link>
                )}
              </div>
            ))}
          </div>

          <p className="border-t border-line px-4 py-2 text-[11px] text-faint">
            Started in this tab. The chassis keeps working even if you close it.
          </p>
        </div>
      )}
    </div>
  );
}
